import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { setMessage } from './message';
import salesOrderService from '../services/sales-order.service';

export const fetchSalesOrdersList = createAsyncThunk(
  'salesOrder/fetchSalesOrdersList',
  async (
    { customer_id, profile_id, page, pagesize, search, ordering },
    thunkAPI,
  ) => {
    try {
      const response = await salesOrderService.getSalesOrdersList(
        customer_id,
        profile_id,
        page,
        pagesize,
        search,
        ordering,
      );
      return response.data;
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  },
);



export const fetchArchivedSalesOrderList = createAsyncThunk(
  'salesOrder/fetchArchivedSalesOrderList',
  async ({ page, pagesize, search, ordering }, thunkAPI) => {
    try {
      const response = await salesOrderService.getArchiveSalesOrderList(
        page,
        pagesize,
        search,
        ordering,
      );
      return response.data;
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  },
);
export const fetchSalesOrderById = createAsyncThunk(
  'salesOrder/fetchSalesOrderById',
  async (salesOrderId, { rejectWithValue }) => {
    try {
      const response = await salesOrderService.getSalesOrderById(salesOrderId);
      return response.data; // Return the profile data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const convertToSalesOrder = createAsyncThunk(
  'salesOrder/convertToSalesOrder',
  async ({ id, values }, thunkAPI) => {
    try {
       const formData = new FormData();

// 1️⃣ Extract and append the main JSON data (excluding file objects)
const { purchase_order_copy, ...cleanData } = values;

formData.append("data", JSON.stringify(cleanData));

// 2️⃣ Append source attachment (if available)
if (values?.purchase_order_copy?.length > 0) {
  const fileObj = values?.purchase_order_copy[0]?.originFileObj;
  if (fileObj) {
    formData.append("purchase_order_copy", fileObj);
  }
}
      const response = await salesOrderService.addQuotationToSalesOrder(id,  formData);
      return response.data;
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const createSalesOrder = createAsyncThunk(
  'salesOrder/createSalesOrder',
  async ({ id, values }, thunkAPI) => {
    try {
      const response = await salesOrderService.addSalesOrder(id, values);
      return response.data;
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  },
);



export const updateSalesOrder = createAsyncThunk(
  'salesOrder/updateSalesOrder',
  async ({ id, values }, thunkAPI) => {
    try {
      const response = await salesOrderService.editSalesOrder(id, values);
      return response.data; // Return the updated die group data
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const deleteSalesOrder = createAsyncThunk(
  'salesOrder/deleteSalesOrder',
  async (id, thunkAPI) => {
    try {
      await salesOrderService.archiveSalesOrder(id); // Ensure `id` is used here
      return id; // Return the ID of the deleted group
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const unArchiveSalesOrder = createAsyncThunk(
  'salesOrder/unArchiveSalesOrder',
  async (id, thunkAPI) => {
    try {
      await salesOrderService.unArchiveSalesOrder(id); // Ensure `id` is used here
      return id; // Return the ID of the deleted group
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  },
);

const initialState = {
  data: [], // List
  list: [],
  totalPages: 0,
  currentPage: 1,
  totalRecord: 0,
  status: 'idle',
  error: null,
  loading: false,
  hasMore: true,
};

const salesOrderSlice = createSlice({
  name: 'salesOrder',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    resetSalesOrderData: state => {
      state.data = []; // This will clear the list
    },
    resetSalesOrderList: state => {
      state.list = []; // This will clear the list
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchSalesOrdersList.pending, state => {
        state.loading = true;
      })
      .addCase(fetchSalesOrdersList.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const fetchedPage = action.payload.page || state.currentPage;
        state.data = action.payload.results.data || []; // Update to match your API response
        state.totalRecord = action.payload.count || 0; // Handle count (total records)
        state.totalPages = action.payload.totalPages || 0; // Handle totalPages
        state.currentPage = action.payload.next ? fetchedPage + 1 : fetchedPage;
        state.list = [...state.list, ...action.payload.results.data];
        state.hasMore = !!action.payload.next;
        state.loading = false;
      })
      .addCase(fetchSalesOrdersList.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
      });
    builder
      .addCase(fetchArchivedSalesOrderList.pending, state => {
        state.status = 'loading';
      })
      .addCase(fetchArchivedSalesOrderList.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const fetchedPage = action.payload.page || state.currentPage;
        state.data = action.payload.results.data || []; // Update to match your API response
        state.totalRecord = action.payload.count || 0; // Handle count (total records)
        state.totalPages = action.payload.totalPages || 0; // Handle totalPages
        state.currentPage = action.payload.next ? fetchedPage + 1 : fetchedPage;
        state.list = [...state.list, ...action.payload.results.data];
        state.hasMore = !!action.payload.next;
      })
      .addCase(fetchArchivedSalesOrderList.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
      });
    builder
      .addCase(fetchSalesOrderById.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSalesOrderById.fulfilled, (state, action) => {
        state.loading = false;
        state.activeProfileRecord = action.payload;
      })
      .addCase(fetchSalesOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
 builder
      .addCase(convertToSalesOrder.pending, state => {
        state.status = 'loading';
      })
      .addCase(convertToSalesOrder.fulfilled, (state, action) => {
        state.status = 'succeeded';
      })
      .addCase(convertToSalesOrder.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      });
    builder
      .addCase(createSalesOrder.pending, state => {
        state.status = 'loading';
      })
      .addCase(createSalesOrder.fulfilled, (state, action) => {
        state.status = 'succeeded';
      })
      .addCase(createSalesOrder.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      });
    builder
      .addCase(updateSalesOrder.pending, state => {
        state.status = 'loading';
      })
      .addCase(updateSalesOrder.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const updateDieProfile = action.payload;
        const index = state.data.findIndex(
          group => group.id === updateDieProfile.id,
        ); // Find the index of the updated group
        if (index !== -1) {
          state.data[index] = updateDieProfile; // Replace the old group with the updated one
        }
      })
      .addCase(updateSalesOrder.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
    builder
      .addCase(deleteSalesOrder.pending, state => {
        state.status = 'loading';
      })
      .addCase(deleteSalesOrder.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = state.data.filter(group => group.id !== action.payload); // Remove the deleted group from state
        state.totalRecord = state.totalRecord - 1;
      })
      .addCase(deleteSalesOrder.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.error.message;
      });
    builder
      .addCase(unArchiveSalesOrder.pending, state => {
        state.status = 'loading';
      })
      .addCase(unArchiveSalesOrder.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = state.data.filter(group => group.id !== action.payload); // Remove the deleted group from state
      })
      .addCase(unArchiveSalesOrder.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const {
  resetSalesOrderData,
  resetSalesOrderList,
} = salesOrderSlice.actions;
export default salesOrderSlice.reducer;
